import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDesktop,
    faClipboardCheck,
    faFileText,
    faChartBar,
    faExclamationTriangle,
    faUsers,
    faClock,
    faCheck,
    faCheckCircle,
    faTimes,
    faArrowRight,
    faEye,
    faChevronDown,
    faChevronUp,
    faBan,
    faToggleOn,
    faToggleOff,
    faPhone,
    faShieldHalved,
    faCalendarDays,
    faXmark,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import api from '../../Api';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);


const RespCatiDashboard = () => {
    const navigate = useNavigate();
    const [surveyProgressData, setSurveyProgressData] = useState({ cati: [], f2f: [], clients: [], available_years: [], total_control_surveys: 0 });
    const [surveyProgressLoading, setSurveyProgressLoading] = useState(false);
    const [spClientFilter, setSpClientFilter] = useState('');
    const [spYearFilter, setSpYearFilter] = useState('');
    const [spSearch, setSpSearch] = useState('');

    const [dashboardData, setDashboardData] = useState({
        posts: {
            totalPosts: 0,
            activePosts: 0,
            assignedPosts: 0
        },
        assignments: {
            totalAssignments: 0,
            activeAssignments: 0,
            completedAssignments: 0
        },
        surveys: {
            totalSurveys: 0,
            todaySubmissions: 0,
            anomaliesToday: 0
        },
        interviewers: {
            totalInterviewers: 0,
            activeInterviewers: 0,
            verifiedInterviewers: 0
        },
        recentAssignments: [],
        recentSurveys: []
    });
    const [productivityData, setProductivityData] = useState({
        daily: [],
        weekly: [],
        top_interviewers: [],
        available_years: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [chartLoading, setChartLoading] = useState(false);
    const [showProductivityCharts, setShowProductivityCharts] = useState(false);
    const [showAssignmentsBlacklist, setShowAssignmentsBlacklist] = useState(false);
    const [assignmentsData, setAssignmentsData] = useState({
        active: 0,
        completed: 0,
        total: 0,
        assignments: []
    });
    const [blacklistData, setBlacklistData] = useState({
        total: 0,
        blacklisted: 0,
        systemActive: true,
        agencies: []
    });
    const [interviewLinesStats, setInterviewLinesStats] = useState({
        surveys: [],
        interviewerRanking: [],
        todayStats: {
            ok: 0,
            refus: 0,
            hors_cible: 0,
            boite_vocal: 0,
            rendez_vous: 0,
            injoignable: 0,
            faux_numero: 0,
            pending: 0
        }
    });

    useEffect(() => {
        fetchDashboardData();
        fetchProductivityData();
        fetchAssignmentsData();
        fetchBlacklistData();
        fetchInterviewLinesStats();
        fetchSurveyProgress('', '', '');
    }, []);

    const spFilterMounted = useRef(false);
    useEffect(() => {
        if (!spFilterMounted.current) { spFilterMounted.current = true; return; }
        fetchSurveyProgress(spClientFilter, spYearFilter, spSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spClientFilter, spYearFilter]);

    useEffect(() => {
        fetchProductivityData();
    }, [selectedYear, selectedMonth]);

    // Handle available years update and adjust selected year if needed
    useEffect(() => {
        if (productivityData.available_years && productivityData.available_years.length > 0) {
            if (!productivityData.available_years.includes(selectedYear)) {
                setSelectedYear(productivityData.available_years[0]);
            }
        }
    }, [productivityData.available_years]);

    const fetchSurveyProgress = async (clientId, year, search) => {
        try {
            setSurveyProgressLoading(true);
            const params = {};
            if (clientId) params.client_id = clientId;
            if (year) params.year = year;
            if (search && search.trim()) params.search = search.trim();
            const response = await api.get('/cati/surveys/progress', { params });
            if (response.data.success) {
                setSurveyProgressData(prev => ({
                    ...response.data.data,
                    clients: response.data.data.clients || prev.clients,
                    available_years: response.data.data.available_years || prev.available_years,
                }));
            }
        } catch (error) {
            console.error('Error fetching survey progress:', error);
        } finally {
            setSurveyProgressLoading(false);
        }
    };

    const fetchProductivityData = async () => {
        try {
            setChartLoading(true);
            const response = await api.get('/cati/dashboard/productivity-stats', {
                params: {
                    year: selectedYear,
                    month: selectedMonth
                }
            });

            if (response.data.success) {
                const data = response.data.data;
                setProductivityData(data);
            }
        } catch (error) {
            console.error('Error fetching productivity stats:', error);
        } finally {
            setChartLoading(false);
        }
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch all CATI data in parallel
            const [statsResponse, assignmentsResponse, surveysResponse] = await Promise.all([
                api.get('/cati/dashboard/stats').catch(err => {
                    console.error('Error fetching CATI stats:', err);
                    return { data: { success: false } };
                }),
                api.get('/cati/assignments?page=1&per_page=5').catch(err => {
                    console.error('Error fetching assignments:', err);
                    return { data: { success: false } };
                }),
                api.get('/cati/surveys?page=1&per_page=5').catch(err => {
                    console.error('Error fetching surveys:', err);
                    return { data: { success: false } };
                })
            ]);

            // Process stats data
            if (statsResponse.data.success) {
                const stats = statsResponse.data.data;
                setDashboardData(prev => ({
                    ...prev,
                    posts: {
                        totalPosts: stats.total_posts || 0,
                        activePosts: stats.active_posts || 0,
                        assignedPosts: stats.assigned_posts || 0
                    },
                    assignments: {
                        totalAssignments: stats.active_assignments || 0,
                        activeAssignments: stats.active_assignments || 0,
                        completedAssignments: 0 // Will be calculated from assignments data
                    },
                    surveys: {
                        totalSurveys: stats.total_surveys || 0,
                        todaySubmissions: stats.today_submissions || 0,
                        anomaliesToday: stats.anomalies_today || 0
                    },
                    interviewers: {
                        totalInterviewers: stats.total_interviewers || 0,
                        activeInterviewers: stats.total_interviewers || 0,
                        verifiedInterviewers: stats.verified_interviewers || 0
                    }
                }));
            }

            // Process assignments data
            if (assignmentsResponse.data.success) {
                setDashboardData(prev => ({
                    ...prev,
                    recentAssignments: assignmentsResponse.data.data.data || []
                }));
            }

            // Process surveys data
            if (surveysResponse.data.success) {
                setDashboardData(prev => ({
                    ...prev,
                    recentSurveys: surveysResponse.data.data.data || []
                }));
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Erreur lors du chargement des données du tableau de bord');
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignmentsData = async () => {
        try {
            const response = await api.get('/cati/assignments?page=1&per_page=100');
            if (response.data.success) {
                const assignments = (response.data.data && response.data.data.data) ? response.data.data.data : [];
                const active = assignments.filter(a => a && !a.ends_at).length;
                const completed = assignments.filter(a => a && a.ends_at).length;
                setAssignmentsData({
                    active,
                    completed,
                    total: assignments.length,
                    assignments: Array.isArray(assignments) ? assignments : []
                });
            }
        } catch (error) {
            console.error('Error fetching assignments data:', error);
            // Set empty arrays on error to prevent undefined issues
            setAssignmentsData({
                active: 0,
                completed: 0,
                total: 0,
                assignments: []
            });
        }
    };

    const fetchBlacklistData = async () => {
        try {
            const [blacklistResponse, settingsResponse] = await Promise.all([
                api.get('/cati/blacklist?per_page=100'),
                api.get('/cati/blacklist/settings')
            ]);

            if (blacklistResponse.data.success) {
                const blacklist = (blacklistResponse.data.data && blacklistResponse.data.data.data) ? blacklistResponse.data.data.data : [];
                const blacklisted = blacklist.filter(b => b && b.is_blacklisted).length;
                setBlacklistData(prev => ({
                    ...prev,
                    total: blacklist.length,
                    blacklisted,
                    agencies: Array.isArray(blacklist) ? blacklist : []
                }));
            }

            if (settingsResponse.data.success) {
                setBlacklistData(prev => ({
                    ...prev,
                    systemActive: settingsResponse.data.data.blacklist_system_active || true
                }));
            }
        } catch (error) {
            console.error('Error fetching blacklist data:', error);
            // Set empty arrays on error to prevent undefined issues
            setBlacklistData(prev => ({
                ...prev,
                total: 0,
                blacklisted: 0,
                agencies: []
            }));
        }
    };

    const fetchInterviewLinesStats = async () => {
        try {
            // Fetch all lines with pagination
            const response = await api.get('/cati/interviewer/lines?per_page=1000');
            
            if (response.data.success) {
                const lines = (response.data.data && response.data.data.data) ? response.data.data.data : [];
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                // Group by survey and get recent 5 surveys
                const bySurvey = {};
                lines.forEach(line => {
                    if (line && line.upload && line.upload.survey) {
                        const surveyId = line.upload.survey.id;
                        const surveyTitle = line.upload.survey.title || 'Unknown';
                        const surveyDate = line.upload.created_at ? new Date(line.upload.created_at) : new Date(0);
                        
                        if (!bySurvey[surveyId]) {
                            bySurvey[surveyId] = {
                                surveyId,
                                surveyTitle,
                                surveyDate,
                                ok: 0,
                                refus: 0,
                                hors_cible: 0,
                                boite_vocal: 0,
                                rendez_vous: 0,
                                injoignable: 0,
                                faux_numero: 0,
                                pending: 0,
                                total: 0
                            };
                        }
                        
                        bySurvey[surveyId].total++;
                        if (line.result_status) {
                            if (bySurvey[surveyId].hasOwnProperty(line.result_status)) {
                                bySurvey[surveyId][line.result_status]++;
                            }
                        } else {
                            bySurvey[surveyId].pending++;
                        }
                    }
                });
                
                // Get top 5 recent surveys by date
                const surveysArray = Object.values(bySurvey)
                    .sort((a, b) => new Date(b.surveyDate) - new Date(a.surveyDate))
                    .slice(0, 5);
                
                // Get monthly interviewer ranking by OK count
                const currentMonth = new Date().getMonth();
                const currentYear = new Date().getFullYear();
                const interviewerStats = {};
                
                lines.forEach(line => {
                    if (line && line.interviewer && line.result_status === 'ok' && line.completed_at) {
                        const completedDate = new Date(line.completed_at);
                        if (completedDate.getMonth() === currentMonth && completedDate.getFullYear() === currentYear) {
                            const interviewerId = line.interviewer.id;
                            if (!interviewerStats[interviewerId]) {
                                interviewerStats[interviewerId] = {
                                    id: interviewerId,
                                    name: line.interviewer.name || 'Unknown',
                                    okCount: 0
                                };
                            }
                            interviewerStats[interviewerId].okCount++;
                        }
                    }
                });
                
                // Get today's statistics
                const todayStats = {
                    ok: 0,
                    refus: 0,
                    hors_cible: 0,
                    boite_vocal: 0,
                    rendez_vous: 0,
                    injoignable: 0,
                    faux_numero: 0,
                    pending: 0
                };
                
                lines.forEach(line => {
                    if (line && line.completed_at) {
                        const completedDate = new Date(line.completed_at);
                        completedDate.setHours(0, 0, 0, 0);
                        
                        if (completedDate.getTime() === today.getTime()) {
                            if (line.result_status && todayStats.hasOwnProperty(line.result_status)) {
                                todayStats[line.result_status]++;
                            }
                        }
                    } else if (line && !line.completed_at) {
                        todayStats.pending++;
                    }
                });
                
                setInterviewLinesStats({
                    surveys: surveysArray,
                    interviewerRanking: Object.values(interviewerStats)
                        .sort((a, b) => b.okCount - a.okCount)
                        .slice(0, 10),
                    todayStats
                });
            }
        } catch (error) {
            console.error('Error fetching interview lines stats:', error);
            setInterviewLinesStats({
                surveys: [],
                interviewerRanking: [],
                todayStats: {
                    ok: 0,
                    refus: 0,
                    hors_cible: 0,
                    boite_vocal: 0,
                    rendez_vous: 0,
                    injoignable: 0,
                    faux_numero: 0,
                    pending: 0
                }
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'completed':
                return 'bg-blue-100 text-blue-800';
            case 'suspended':
                return 'bg-yellow-100 text-yellow-800';
            case 'inactive':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active':
                return 'Actif';
            case 'completed':
                return 'Terminé';
            case 'suspended':
                return 'Suspendu';
            case 'inactive':
                return 'Inactif';
            default:
                return status;
        }
    };

    // Helper function to get all months
    const getAllMonths = () => {
        const months = [
            { value: 1, label: 'Janvier' },
            { value: 2, label: 'Février' },
            { value: 3, label: 'Mars' },
            { value: 4, label: 'Avril' },
            { value: 5, label: 'Mai' },
            { value: 6, label: 'Juin' },
            { value: 7, label: 'Juillet' },
            { value: 8, label: 'Août' },
            { value: 9, label: 'Septembre' },
            { value: 10, label: 'Octobre' },
            { value: 11, label: 'Novembre' },
            { value: 12, label: 'Décembre' }
        ];
        return months;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement du tableau de bord...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-600 mb-4" />
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    const totalCati = surveyProgressData.cati.length;
    const totalControlled = surveyProgressData.cati_control_surveys || 0;
    const spHasFilters = spClientFilter || spYearFilter || spSearch;

    const renderSurveyCard = (survey) => {
        const completionPct = survey.general_target ? Math.min(100, Math.round((survey.completions / survey.general_target) * 100)) : null;
        const controlPct = survey.general_target > 0 ? Math.min(100, Math.round((survey.controls_done / survey.general_target) * 100)) : 0;
        const controlTargetPct = survey.control_percentage ? Math.min(100, parseFloat(survey.control_percentage)) : null;
        const humanCount = survey.controls_done_human ?? survey.controls_done ?? 0;
        const aiOnlyCount = survey.controls_done_ai_only ?? 0;
        const humanWidth = survey.general_target > 0 ? Math.min(100, (humanCount / survey.general_target) * 100) : 0;
        const aiWidth = survey.general_target > 0 ? Math.min(100 - humanWidth, (aiOnlyCount / survey.general_target) * 100) : 0;
        const totalDoneCount = humanCount + aiOnlyCount;
        const humanPct = totalDoneCount > 0 ? Math.round((humanCount / totalDoneCount) * 100) : 0;
        const aiPct = totalDoneCount > 0 ? Math.round((aiOnlyCount / totalDoneCount) * 100) : 0;
        const budgetPct = survey.has_budget && survey.total_budget > 0 ? Math.min(100, Math.round((survey.budget_spent / survey.total_budget) * 1000) / 10) : null;

        return (
            <div
                key={survey.id}
                className="group bg-white rounded-xl border border-gray-200 p-4 sm:p-5 cursor-pointer hover:shadow-lg hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-200"
                onClick={() => navigate(`/cati/surveys/${survey.id}/stats`)}
            >
                {/* Header */}
                <div className="mb-3">
                    <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900 leading-tight break-words group-hover:text-red-700 transition-colors min-w-0">
                            {survey.title}
                        </h4>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                            CATI
                        </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {survey.client_name && (
                            <span className="text-xs text-gray-500">{survey.client_name}</span>
                        )}
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <FontAwesomeIcon icon={faCalendarDays} className="text-[9px]" />
                            {survey.date_debut} - {survey.date_fin}
                        </span>
                    </div>
                </div>

                {/* Progress bars */}
                <div className="space-y-3">
                    {/* Completion */}
                    <div>
                        <div className="flex items-center justify-between text-[11px] sm:text-xs mb-1.5">
                            <span className="text-gray-500">Complétions</span>
                            <span className="font-semibold text-gray-700 tabular-nums">
                                {survey.completions.toLocaleString('fr-FR')}
                                {survey.general_target ? <span className="text-gray-400 font-normal"> / {survey.general_target.toLocaleString('fr-FR')}</span> : ''}
                                {completionPct !== null && <span className="ml-1 sm:ml-1.5 text-gray-400 font-normal">({completionPct}%)</span>}
                            </span>
                        </div>
                        {survey.general_target > 0 && (
                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                    className="h-2 rounded-full transition-all duration-700 ease-out"
                                    style={{
                                        width: `${completionPct}%`,
                                        background: completionPct >= 80
                                            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                            : completionPct >= 50
                                            ? 'linear-gradient(90deg, #facc15, #eab308)'
                                            : 'linear-gradient(90deg, #f87171, #dc2626)'
                                    }}
                                ></div>
                            </div>
                        )}
                    </div>

                    {/* Control */}
                    <div>
                        <div className="flex items-center justify-between text-[11px] sm:text-xs mb-1.5">
                            <span className="text-gray-500 flex-shrink-0">
                                Contrôle
                                {controlTargetPct != null && (
                                    <span className="hidden sm:inline text-gray-400 font-normal"> (objectif {controlTargetPct}%)</span>
                                )}
                            </span>
                            <span className="font-semibold text-gray-700 tabular-nums text-right">
                                {survey.controls_done.toLocaleString('fr-FR')}
                                <span className="text-gray-400 font-normal"> / {survey.general_target ? survey.general_target.toLocaleString('fr-FR') : '—'}</span>
                                {survey.general_target > 0 && <span className="ml-1 sm:ml-1.5 text-gray-400 font-normal">({controlPct}%)</span>}
                            </span>
                        </div>
                        {survey.general_target > 0 && (
                            <div className="relative w-full bg-gray-100 rounded-full h-2 flex overflow-hidden">
                                <div
                                    className="h-2 transition-all duration-700 ease-out"
                                    style={{
                                        width: `${humanWidth}%`,
                                        background: controlTargetPct != null && controlPct >= controlTargetPct && aiOnlyCount === 0
                                            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                            : 'linear-gradient(90deg, #60a5fa, #3b82f6)'
                                    }}
                                    title={`Humain: ${humanCount} (${humanPct}%)`}
                                ></div>
                                <div
                                    className="h-2 transition-all duration-700 ease-out"
                                    style={{
                                        width: `${aiWidth}%`,
                                        background: 'linear-gradient(90deg, #818cf8, #6366f1)'
                                    }}
                                    title={`IA: ${aiOnlyCount} (${aiPct}%)`}
                                ></div>
                                {controlTargetPct != null && controlTargetPct > 0 && (
                                    <div className="absolute top-1/2 -translate-y-1/2 pointer-events-none" style={{ left: `${controlTargetPct}%` }}>
                                        <div className="w-0.5 h-4 bg-red-500 rounded-full -ml-px"></div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {survey.has_budget ? (
                        <div>
                            <div className="flex items-center justify-between text-[11px] sm:text-xs mb-1.5">
                                <span className="text-gray-500">Budget</span>
                                <span className="font-semibold text-gray-700 tabular-nums text-right">
                                    {Number(survey.budget_spent).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                    <span className="text-gray-400 font-normal"> / {Number(survey.total_budget).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD</span>
                                    {budgetPct !== null && <span className="ml-1 sm:ml-1.5 text-gray-400 font-normal">({budgetPct}%)</span>}
                                </span>
                            </div>
                            {budgetPct !== null && (
                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                    <div className="h-2 rounded-full transition-all duration-700 ease-out" style={{
                                        width: `${budgetPct}%`,
                                        background: budgetPct >= 90 ? 'linear-gradient(90deg, #f87171, #dc2626)' : budgetPct >= 70 ? 'linear-gradient(90deg, #facc15, #eab308)' : 'linear-gradient(90deg, #22c55e, #16a34a)'
                                    }}></div>
                                </div>
                            )}
                            {/* Category breakdown */}
                            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                                {survey.budget_enqueteur > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Enquêteurs</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_enqueteur).toLocaleString('fr-FR')} / {Number(survey.budget_enqueteur).toLocaleString('fr-FR')}</span></div>
                                )}
                                {survey.budget_controle > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Contrôle</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_controle).toLocaleString('fr-FR')} / {Number(survey.budget_controle).toLocaleString('fr-FR')}</span></div>
                                )}
                                {survey.budget_deplacement > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Déplacement</span><span className="text-gray-600 tabular-nums">{Number(survey.spent_deplacement).toLocaleString('fr-FR')} / {Number(survey.budget_deplacement).toLocaleString('fr-FR')}</span></div>
                                )}
                                {survey.budget_imprevus > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-400">Imprévus</span><span className={`tabular-nums ${survey.imprevus_consumed > survey.budget_imprevus ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>{Number(survey.imprevus_consumed).toLocaleString('fr-FR')} / {Number(survey.budget_imprevus).toLocaleString('fr-FR')}</span></div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-[11px] text-gray-400 italic">Budget non encore défini</div>
                    )}
                </div>

                {/* Footer hint */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end">
                    <span className="text-[11px] text-gray-400 group-hover:text-red-500 transition-colors flex items-center gap-1">
                        Voir détails <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="p-4 sm:p-6 lg:p-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                Tableau de Bord CATI
                            </h1>
                            <p className="text-gray-600">
                                Gestion des postes, assignations et productivité du système CATI
                            </p>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
                        {/* CATI Projects */}
                        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-2 sm:mb-3">
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faPhone} className="text-gray-600 text-xs sm:text-sm" />
                                </div>
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold bg-gray-100 text-gray-600 uppercase tracking-wide">CATI</span>
                            </div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{totalCati}</div>
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Enquêtes en cours</div>
                        </div>

                        {/* Surveys with active control */}
                        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-2 sm:mb-3">
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faShieldHalved} className="text-gray-600 text-xs sm:text-sm" />
                                </div>
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold bg-gray-100 text-gray-600 uppercase tracking-wide">Contrôle</span>
                            </div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{totalControlled}</div>
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Enquêtes en contrôle actif</div>
                        </div>
                    </div>

                    {/* Survey Progress Section */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
                        {/* Section Header */}
                        <div className="px-4 sm:px-6 pt-5 pb-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                    Progression des Enquêtes
                                </h3>
                            </div>

                            {/* Filters */}
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5 pb-4 border-b border-gray-100">
                                <div className="relative flex-1">
                                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par titre, ID ou client..."
                                        value={spSearch}
                                        onChange={(e) => setSpSearch(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && fetchSurveyProgress(spClientFilter, spYearFilter, spSearch)}
                                        className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 focus:bg-white transition-all"
                                    />
                                </div>
                                <select
                                    value={spClientFilter}
                                    onChange={(e) => setSpClientFilter(e.target.value)}
                                    className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 focus:bg-white transition-all"
                                >
                                    <option value="">Tous les clients</option>
                                    {surveyProgressData.clients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                                <select
                                    value={spYearFilter}
                                    onChange={(e) => setSpYearFilter(e.target.value)}
                                    className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 focus:bg-white transition-all"
                                >
                                    <option value="">Toutes les années</option>
                                    {surveyProgressData.available_years.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => fetchSurveyProgress(spClientFilter, spYearFilter, spSearch)}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 active:scale-95 transition-all flex-shrink-0 shadow-sm"
                                >
                                    <FontAwesomeIcon icon={faSearch} />
                                </button>
                                {spHasFilters && (
                                    <button
                                        onClick={() => { setSpClientFilter(''); setSpYearFilter(''); setSpSearch(''); fetchSurveyProgress('', '', ''); }}
                                        className="px-3 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 active:scale-95 transition-all flex-shrink-0"
                                    >
                                        <FontAwesomeIcon icon={faXmark} className="mr-1" />
                                        Effacer
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Survey List */}
                        <div className="px-4 sm:px-6 py-5">
                            {surveyProgressLoading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="w-8 h-8 border-2 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
                                    <p className="mt-3 text-sm text-gray-400">Chargement...</p>
                                </div>
                            ) : surveyProgressData.cati.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-3xl mb-3 text-gray-300" />
                                    <p className="text-sm font-medium text-gray-500">Aucune enquête trouvée</p>
                                    {!spHasFilters && (
                                        <p className="text-xs mt-1">Les enquêtes actives avec des dates incluant aujourd'hui apparaîtront ici</p>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {surveyProgressData.cati.map(survey => renderSurveyCard(survey))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Left Column - Main Content */}
                        <div className="lg:col-span-8 space-y-6">
                            {/* Overview Stats */}
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {/* Posts Stats */}
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-red-100 rounded-full">
                                            <FontAwesomeIcon icon={faDesktop} className="text-red-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Total Posts</p>
                                            <p className="text-2xl font-bold text-gray-900">{dashboardData.posts.totalPosts}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Assignments Stats */}
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-red-100 rounded-full">
                                            <FontAwesomeIcon icon={faClipboardCheck} className="text-red-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Assignations Actives</p>
                                            <p className="text-2xl font-bold text-gray-900">{dashboardData.assignments.activeAssignments}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Surveys Stats */}
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-red-100 rounded-full">
                                            <FontAwesomeIcon icon={faFileText} className="text-red-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Enquêtes Actives</p>
                                            <p className="text-2xl font-bold text-gray-900">{dashboardData.surveys.totalSurveys}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Productivity Charts - Collapsible Section */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <button
                                    onClick={() => setShowProductivityCharts(!showProductivityCharts)}
                                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                >
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Graphiques de Productivité
                                    </h3>
                                    <FontAwesomeIcon 
                                        icon={showProductivityCharts ? faChevronUp : faChevronDown} 
                                        className="text-gray-500 transition-transform duration-200"
                                    />
                                </button>
                                
                                {showProductivityCharts && (
                                    <div className="px-6 pb-6 border-t border-gray-100 space-y-6 pt-6">
                                        {/* Weekly Productivity Chart - First */}
                                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-3 sm:space-y-0">
                                                <h3 className="text-lg font-semibold text-gray-900">Productivité Hebdomadaire</h3>
                                                {chartLoading && (
                                                    <div className="text-sm text-gray-500">Chargement...</div>
                                                )}
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                                                    {/* Year Filter */}
                                                    <select
                                                        value={selectedYear}
                                                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                        className="w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                    >
                                                        {productivityData.available_years && productivityData.available_years.length > 0 ? (
                                                            productivityData.available_years.map(year => (
                                                                <option key={year} value={year}>{year}</option>
                                                            ))
                                                        ) : (
                                                            <option value={selectedYear}>Chargement...</option>
                                                        )}
                                                    </select>
                                                    
                                                    {/* Month Filter */}
                                                    <select
                                                        value={selectedMonth}
                                                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                                        className="w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                    >
                                                        {getAllMonths().map(month => (
                                                            <option key={month.value} value={month.value}>{month.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            {productivityData.weekly && productivityData.weekly.length > 0 ? (
                                                <Bar
                                                    data={{
                                                        labels: productivityData.weekly.map(item => item.week),
                                                        datasets: [
                                                            {
                                                                label: 'Complétées',
                                                                data: productivityData.weekly.map(item => item.completes),
                                                                backgroundColor: 'rgba(239, 68, 68, 0.8)',
                                                                borderColor: 'rgb(239, 68, 68)',
                                                                borderWidth: 1
                                                            }
                                                        ]
                                                    }}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: true,
                                                        aspectRatio: 2,
                                                        plugins: {
                                                            legend: {
                                                                display: true,
                                                                position: 'top'
                                                            },
                                                            tooltip: {
                                                                callbacks: {
                                                                    afterLabel: function(context) {
                                                                        const weekData = productivityData.weekly[context.dataIndex];
                                                                        return `Enquêteurs: ${weekData.interviewers} | Posts: ${weekData.posts}`;
                                                                    }
                                                                }
                                                            }
                                                        },
                                                        scales: {
                                                            y: {
                                                                beginAtZero: true,
                                                                ticks: {
                                                                    stepSize: 1
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            ) : !chartLoading && (
                                                <div className="text-center py-12 text-gray-500">
                                                    Aucune donnée de productivité disponible pour ce mois
                                                </div>
                                            )}
                                        </div>

                                        {/* Daily Chart - Full width */}
                            {productivityData.daily && productivityData.daily.length > 0 && (
                                <div>
                                    {/* Daily Productivity Chart */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                            Productivité Quotidienne
                                        </h3>
                                        <Line
                                            data={{
                                                labels: productivityData.daily.map(item => {
                                                    const date = new Date(item.date);
                                                    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                                                }),
                                                datasets: [
                                                    {
                                                        label: 'Complétées',
                                                        data: productivityData.daily.map(item => item.completes),
                                                        borderColor: 'rgb(239, 68, 68)',
                                                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                        tension: 0.4,
                                                        fill: true
                                                    }
                                                ]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: true,
                                                aspectRatio: 2.5,
                                                plugins: {
                                                    legend: {
                                                        display: true,
                                                        position: 'top'
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: function(context) {
                                                                return `Complétées: ${context.parsed.y}`;
                                                            }
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        beginAtZero: true,
                                                        ticks: {
                                                            stepSize: 1
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                        </div>
                            )}
                                    </div>
                                )}
                            </div>

                            {/* Assignments & Blacklist - Collapsible Section */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <button
                                    onClick={() => setShowAssignmentsBlacklist(!showAssignmentsBlacklist)}
                                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                >
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Affectations CATI & Liste Noire des Agences
                                    </h3>
                                    <FontAwesomeIcon 
                                        icon={showAssignmentsBlacklist ? faChevronUp : faChevronDown} 
                                        className="text-gray-500 transition-transform duration-200"
                                    />
                                </button>
                                
                                {showAssignmentsBlacklist && (
                                    <div className="px-6 pb-6 border-t border-gray-100 space-y-6 pt-6">
                                        {/* Assignments Overview */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="text-sm text-gray-600">Total Assignations</div>
                                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-gray-400" />
                                                </div>
                                                <div className="text-3xl font-bold text-gray-900">
                                                    {assignmentsData.total}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-2">Toutes les assignations</div>
                                            </div>

                                            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="text-sm text-gray-600">Actives</div>
                                                    <FontAwesomeIcon icon={faCheck} className="text-green-500" />
                                                </div>
                                                <div className="text-3xl font-bold text-green-600">
                                                    {assignmentsData.active}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-2">En cours</div>
                                            </div>

                                            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="text-sm text-gray-600">Terminées</div>
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500" />
                                                </div>
                                                <div className="text-3xl font-bold text-blue-600">
                                                    {assignmentsData.completed}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-2">Complétées</div>
                                            </div>
                                        </div>

                                        {/* Blacklist Overview */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                            <div className="flex items-center justify-between mb-6">
                                                <h4 className="text-lg font-semibold text-gray-900">Liste Noire des Agences</h4>
                                                <Link 
                                                    to="/cati/blacklist" 
                                                    className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center"
                                                >
                                                    Gérer
                                                    <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
                                                </Link>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-sm text-gray-600">Total Agences</div>
                                                        <FontAwesomeIcon icon={faBan} className="text-gray-400" />
                                                    </div>
                                                    <div className="text-2xl font-bold text-gray-900">
                                                        {blacklistData.total}
                                                    </div>
                                                </div>

                                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-sm text-gray-600">Blacklistées</div>
                                                        <FontAwesomeIcon icon={faBan} className="text-red-500" />
                                                    </div>
                                                    <div className="text-2xl font-bold text-red-600">
                                                        {blacklistData.blacklisted}
                                                    </div>
                                                </div>

                                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-sm text-gray-600">Système</div>
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    const newValue = !blacklistData.systemActive;
                                                                    await api.put('/cati/blacklist/settings', {
                                                                        blacklist_system_active: newValue
                                                                    });
                                                                    setBlacklistData(prev => ({ ...prev, systemActive: newValue }));
                                                                } catch (error) {
                                                                    console.error('Error updating blacklist settings:', error);
                                                                }
                                                            }}
                                                            className="text-2xl"
                                                        >
                                                            <FontAwesomeIcon 
                                                                icon={blacklistData.systemActive ? faToggleOn : faToggleOff} 
                                                                className={blacklistData.systemActive ? 'text-green-500' : 'text-gray-400'}
                                                            />
                                                        </button>
                                    </div>
                                                    <div className="text-sm font-medium text-gray-700">
                                                        {blacklistData.systemActive ? 'Actif' : 'Inactif'}
                                </div>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <p className="text-sm text-gray-600">
                                                    <strong>Note:</strong> Le système de blacklist empêche les enquêteurs de recevoir des lignes des agences qui ont atteint 3 statuts "OK". 
                                                    Vous pouvez activer/désactiver le système ci-dessus.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Charts Section */}
                                        {/* Today's Statistics Chart - First */}
                                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                                Statistiques d'Aujourd'hui
                                            </h4>
                                            <Bar
                                                data={{
                                                    labels: ['OK', 'Refus', 'Hors Cible', 'Boîte Vocale', 'Rendez-vous', 'Injoignable', 'Faux Numéro', 'En Attente'],
                                                    datasets: [
                                                        {
                                                            label: 'Nombre de lignes',
                                                            data: [
                                                                interviewLinesStats.todayStats.ok,
                                                                interviewLinesStats.todayStats.refus,
                                                                interviewLinesStats.todayStats.hors_cible,
                                                                interviewLinesStats.todayStats.boite_vocal,
                                                                interviewLinesStats.todayStats.rendez_vous,
                                                                interviewLinesStats.todayStats.injoignable,
                                                                interviewLinesStats.todayStats.faux_numero,
                                                                interviewLinesStats.todayStats.pending
                                                            ],
                                                            backgroundColor: [
                                                                'rgba(34, 197, 94, 0.8)',
                                                                'rgba(239, 68, 68, 0.8)',
                                                                'rgba(249, 115, 22, 0.8)',
                                                                'rgba(59, 130, 246, 0.8)',
                                                                'rgba(168, 85, 247, 0.8)',
                                                                'rgba(234, 179, 8, 0.8)',
                                                                'rgba(107, 114, 128, 0.8)',
                                                                'rgba(156, 163, 175, 0.8)'
                                                            ],
                                                            borderColor: [
                                                                'rgb(34, 197, 94)',
                                                                'rgb(239, 68, 68)',
                                                                'rgb(249, 115, 22)',
                                                                'rgb(59, 130, 246)',
                                                                'rgb(168, 85, 247)',
                                                                'rgb(234, 179, 8)',
                                                                'rgb(107, 114, 128)',
                                                                'rgb(156, 163, 175)'
                                                            ],
                                                            borderWidth: 1
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: true,
                                                    aspectRatio: 2,
                                                    plugins: {
                                                        legend: {
                                                            display: false
                                                        },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function(context) {
                                                                    return `${context.parsed.y} ligne(s)`;
                                                                }
                                                            }
                                                        }
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            ticks: {
                                                                stepSize: 1
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>

                                        {/* Status by Survey Chart - Stacked Bar */}
                                        {interviewLinesStats.surveys && interviewLinesStats.surveys.length > 0 && (
                                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                                    Statuts des Lignes par Enquête (5 Enquêtes Récentes)
                                                </h4>
                                                <Bar
                                                    data={{
                                                        labels: interviewLinesStats.surveys.map(s => 
                                                            s.surveyTitle.length > 30 ? s.surveyTitle.substring(0, 30) + '...' : s.surveyTitle
                                                        ),
                                                        datasets: [
                                                            {
                                                                label: 'OK',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.ok || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(34, 197, 94, 0.8)',
                                                                borderColor: 'rgb(34, 197, 94)',
                                                                borderWidth: 1
                                                            },
                                                            {
                                                                label: 'Refus',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.refus || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(239, 68, 68, 0.8)',
                                                                borderColor: 'rgb(239, 68, 68)',
                                                                borderWidth: 1
                                                            },
                                                            {
                                                                label: 'Hors Cible',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.hors_cible || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(249, 115, 22, 0.8)',
                                                                borderColor: 'rgb(249, 115, 22)',
                                                                borderWidth: 1
                                                            },
                                                            {
                                                                label: 'Boîte Vocale',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.boite_vocal || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(59, 130, 246, 0.8)',
                                                                borderColor: 'rgb(59, 130, 246)',
                                                                borderWidth: 1
                                                            },
                                                            {
                                                                label: 'Rendez-vous',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.rendez_vous || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(168, 85, 247, 0.8)',
                                                                borderColor: 'rgb(168, 85, 247)',
                                                                borderWidth: 1
                                                            },
                                                            {
                                                                label: 'Injoignable',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.injoignable || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(234, 179, 8, 0.8)',
                                                                borderColor: 'rgb(234, 179, 8)',
                                                                borderWidth: 1
                                                            },
                                                            {
                                                                label: 'Faux Numéro',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.faux_numero || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(107, 114, 128, 0.8)',
                                                                borderColor: 'rgb(107, 114, 128)',
                                                                borderWidth: 1
                                                            },
                                                            {
                                                                label: 'En Attente',
                                                                data: interviewLinesStats.surveys.map(s => s.total > 0 ? parseFloat(((s.pending || 0) / s.total * 100).toFixed(1)) : 0),
                                                                backgroundColor: 'rgba(156, 163, 175, 0.8)',
                                                                borderColor: 'rgb(156, 163, 175)',
                                                                borderWidth: 1
                                                            }
                                                        ]
                                                    }}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: true,
                                                        aspectRatio: 2.5,
                                                        plugins: {
                                                            legend: {
                                                                display: true,
                                                                position: 'top'
                                                            },
                                                            tooltip: {
                                                                callbacks: {
                                                                    label: function(context) {
                                                                        return `${context.dataset.label}: ${context.parsed.y}%`;
                                                                    }
                                                                }
                                                            }
                                                        },
                                                        scales: {
                                                            x: {
                                                                stacked: true
                                                            },
                                                            y: {
                                                                stacked: true,
                                                                beginAtZero: true,
                                                                max: 100,
                                                                ticks: {
                                                                    callback: function(value) {
                                                                        return value + '%';
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Monthly Interviewer Ranking Chart */}
                                        {interviewLinesStats.interviewerRanking && interviewLinesStats.interviewerRanking.length > 0 && (
                                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                                    Classement Mensuel des Enquêteurs par OK
                                                </h4>
                                                <Bar
                                                    data={{
                                                        labels: interviewLinesStats.interviewerRanking.map(i => i.name),
                                                        datasets: [
                                                            {
                                                                label: 'Nombre d\'OK',
                                                                data: interviewLinesStats.interviewerRanking.map(i => i.okCount),
                                                                backgroundColor: interviewLinesStats.interviewerRanking.map((_, index) => {
                                                                    const colors = [
                                                                        'rgba(20, 184, 166, 0.9)', // Teal
                                                                        'rgba(132, 204, 22, 0.9)', // Lime green
                                                                        'rgba(234, 179, 8, 0.9)', // Yellow
                                                                        'rgba(249, 115, 22, 0.9)', // Orange
                                                                        'rgba(239, 68, 68, 0.9)', // Red
                                                                        'rgba(168, 85, 247, 0.9)', // Purple
                                                                        'rgba(59, 130, 246, 0.9)', // Blue
                                                                        'rgba(236, 72, 153, 0.9)', // Pink
                                                                        'rgba(34, 197, 94, 0.9)', // Green
                                                                        'rgba(107, 114, 128, 0.9)' // Gray
                                                                    ];
                                                                    return colors[index % colors.length];
                                                                }),
                                                                borderColor: interviewLinesStats.interviewerRanking.map((_, index) => {
                                                                    const colors = [
                                                                        'rgb(20, 184, 166)', // Teal
                                                                        'rgb(132, 204, 22)', // Lime green
                                                                        'rgb(234, 179, 8)', // Yellow
                                                                        'rgb(249, 115, 22)', // Orange
                                                                        'rgb(239, 68, 68)', // Red
                                                                        'rgb(168, 85, 247)', // Purple
                                                                        'rgb(59, 130, 246)', // Blue
                                                                        'rgb(236, 72, 153)', // Pink
                                                                        'rgb(34, 197, 94)', // Green
                                                                        'rgb(107, 114, 128)' // Gray
                                                                    ];
                                                                    return colors[index % colors.length];
                                                                }),
                                                                borderWidth: 2,
                                                                borderRadius: 8,
                                                                borderSkipped: false
                                                            }
                                                        ]
                                                    }}
                                                    options={{
                                                        indexAxis: 'y',
                                                        responsive: true,
                                                        maintainAspectRatio: true,
                                                        aspectRatio: 1.5,
                                                        plugins: {
                                                            legend: {
                                                                display: false
                                                            },
                                                            tooltip: {
                                                                callbacks: {
                                                                    label: function(context) {
                                                                        return `${context.parsed.x} OK`;
                                                                    },
                                                                    title: function(context) {
                                                                        return context[0].label;
                                                                    }
                                                                },
                                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                                padding: 12,
                                                                titleFont: {
                                                                    size: 14,
                                                                    weight: 'bold'
                                                                },
                                                                bodyFont: {
                                                                    size: 13
                                                                }
                                                            }
                                                        },
                                                        scales: {
                                                            x: {
                                                                beginAtZero: true,
                                                                ticks: {
                                                                    stepSize: 1,
                                                                    font: {
                                                                        size: 12
                                                                    }
                                                                },
                                                                grid: {
                                                                    display: true,
                                                                    color: 'rgba(0, 0, 0, 0.05)'
                                                                }
                                                            },
                                                            y: {
                                                                ticks: {
                                                                    font: {
                                                                        size: 12,
                                                                        weight: '500'
                                                                    },
                                                                    color: '#374151'
                                                                },
                                                                grid: {
                                                                    display: false
                                                                }
                                                            }
                                                        },
                                                        layout: {
                                                            padding: {
                                                                left: 10,
                                                                right: 20,
                                                                top: 10,
                                                                bottom: 10
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Quick Actions */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Link 
                                                to="/cati/assignments" 
                                                className="bg-white rounded-lg p-4 border border-gray-200 hover:border-red-500 hover:shadow-md transition-all flex items-center justify-between group"
                                            >
                                                <div className="flex items-center">
                                                    <div className="p-2 bg-red-100 rounded-lg mr-3">
                                                        <FontAwesomeIcon icon={faClipboardCheck} className="text-red-600" />
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-900">Gérer les Assignations</div>
                                                        <div className="text-sm text-gray-500">Voir et gérer toutes les assignations</div>
                                                    </div>
                                                </div>
                                                <FontAwesomeIcon icon={faArrowRight} className="text-gray-400 group-hover:text-red-600 transition-colors" />
                                            </Link>

                                            <Link 
                                                to="/cati/blacklist" 
                                                className="bg-white rounded-lg p-4 border border-gray-200 hover:border-red-500 hover:shadow-md transition-all flex items-center justify-between group"
                                            >
                                                <div className="flex items-center">
                                                    <div className="p-2 bg-red-100 rounded-lg mr-3">
                                                        <FontAwesomeIcon icon={faBan} className="text-red-600" />
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-900">Gérer la Liste Noire</div>
                                                        <div className="text-sm text-gray-500">Voir et gérer les agences blacklistées</div>
                                                    </div>
                                                </div>
                                                <FontAwesomeIcon icon={faArrowRight} className="text-gray-400 group-hover:text-red-600 transition-colors" />
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Recent Assignments */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Assignations Récentes</h3>
                                    <Link 
                                        to="/cati/assignments" 
                                        className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center"
                                    >
                                        Voir tout
                                        <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
                                    </Link>
                                </div>
                                
                                {dashboardData.recentAssignments.length > 0 ? (
                                    <div className="space-y-3">
                                        {dashboardData.recentAssignments.slice(0, 3).map((assignment) => (
                                            <div key={assignment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center">
                                                    <FontAwesomeIcon icon={faDesktop} className="text-blue-500 mr-3" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">
                                                            Poste {assignment.post?.number}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            {assignment.interviewer?.name} • {formatDate(assignment.starts_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assignment.ends_at ? 'completed' : 'active')}`}>
                                                    {assignment.ends_at ? 'Terminée' : 'Active'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">Aucune assignation récente</p>
                                )}
                            </div>
                        </div>

                        {/* Right Column - Side Stats */}
                        <div className="lg:col-span-4 space-y-6">
                            {/* Enquêteurs Overview */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Enquêteurs CATI</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Total Enquêteurs</span>
                                        <span className="font-semibold text-gray-900">{dashboardData.interviewers.totalInterviewers}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Actifs</span>
                                        <span className="font-semibold text-red-600">{dashboardData.interviewers.activeInterviewers}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Vérifiés</span>
                                        <span className="font-semibold text-red-600">{dashboardData.interviewers.verifiedInterviewers}</span>
                                    </div>
                                </div>
                                <Link 
                                    to="/cati/interviewer/create" 
                                    className="mt-4 w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-center block"
                                >
                                    Nouvel Enquêteur
                                </Link>
                            </div>

                            {/* Anomalies Overview */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Anomalies Aujourd'hui</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Anomalies Détectées</span>
                                        <span className="font-semibold text-red-600">{dashboardData.surveys.anomaliesToday}</span>
                                    </div>
                                </div>
                                <Link 
                                    to="/cati/anomalies" 
                                    className="mt-4 w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors text-center block"
                                >
                                    Voir les Anomalies
                                </Link>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RespCatiDashboard;
